/**
 * Nome POPULAR de aeroporto → IATA ("Guarulhos" → GRU, "Galeão" → GIG), e o
 * caminho de volta, IATA → nome de EXIBIÇÃO ("GRU" → "Guarulhos"). O nome é o
 * que o cliente fala quando quer UM aeroporto de uma cidade com vários — e que
 * nenhuma tabela IATA→cidade (`airport_cities*`) consegue devolver (elas vão no
 * sentido oposto). Consumidores: os cortes de aeroporto de saída/chegada e de
 * conexão do agente de voo (`apps/services/agents/.../logic/airport_terms.ts`)
 * e a cena do mapper (`.../mapper/scene_place.ts`).
 *
 * UMA curadoria só (`AIRPORTS_BY_NAME`): cada entrada traz o IATA, o nome de
 * EXIBIÇÃO (com acento) e os nomes de BUSCA já DOBRADOS (`foldAirportName`:
 * sem acento, minúscula, espaços colapsados, sem o prefixo "aeroporto
 * (internacional) de/do/da"), incluindo sinônimos que apontam pro mesmo IATA
 * ("cumbica"/"guarulhos" → GRU). As duas direções são DERIVADAS dela, então
 * não existe a divergência que duas tabelas paralelas permitiam: uma curadoria
 * nova sai nas duas pontas por construção.
 *
 * Curadoria mínima: os aeroportos das metros brasileiras + os internacionais que
 * um viajante corporativo brasileiro nomeia. Só aeroportos REAIS (nunca código
 * metro) — invariante coberta por teste.
 *
 * Nomear um SEGUNDO aeroporto de uma cidade sem código metro tem efeito além do
 * nome: a cidade passa a pedir ESCOLHA (`airportChoicesOfCity`), e quem diz só o
 * nome dela ("Dallas") recebe a pergunta em vez de um aeroporto escolhido pela
 * ordem do fornecedor (#4157). A ordem das entradas de uma cidade é a ordem em que
 * a pergunta as lista: o aeroporto internacional primeiro.
 *
 * Pure (no IO): compile-time table lookup.
 */

interface CuratedAirport {
  readonly iata: string;
  readonly display: string;
  readonly names: readonly string[];
}

const AIRPORTS_BY_NAME: ReadonlyArray<CuratedAirport> = [
  { iata: 'GRU', display: 'Guarulhos', names: ['guarulhos', 'cumbica'] },
  { iata: 'CGH', display: 'Congonhas', names: ['congonhas'] },
  { iata: 'VCP', display: 'Viracopos', names: ['viracopos'] },
  { iata: 'GIG', display: 'Galeão', names: ['galeao', 'tom jobim'] },
  { iata: 'SDU', display: 'Santos Dumont', names: ['santos dumont'] },
  { iata: 'CNF', display: 'Confins', names: ['confins', 'tancredo neves'] },
  { iata: 'PLU', display: 'Pampulha', names: ['pampulha'] },
  { iata: 'BSB', display: 'Juscelino Kubitschek', names: ['juscelino kubitschek'] },
  { iata: 'CWB', display: 'Afonso Pena', names: ['afonso pena'] },
  { iata: 'POA', display: 'Salgado Filho', names: ['salgado filho'] },
  { iata: 'FLN', display: 'Hercílio Luz', names: ['hercilio luz'] },
  { iata: 'FOR', display: 'Pinto Martins', names: ['pinto martins'] },
  { iata: 'REC', display: 'Guararapes', names: ['guararapes', 'gilberto freyre'] },
  { iata: 'MAO', display: 'Eduardo Gomes', names: ['eduardo gomes'] },
  { iata: 'BEL', display: 'Val de Cans', names: ['val de cans'] },
  {
    iata: 'JFK',
    display: 'John F. Kennedy',
    names: ['john f. kennedy', 'john f kennedy', 'kennedy'],
  },
  { iata: 'EWR', display: 'Newark', names: ['newark'] },
  { iata: 'LGA', display: 'LaGuardia', names: ['la guardia', 'laguardia'] },
  // Cidades com mais de um aeroporto comercial e SEM código metro no fornecedor
  // (#4157): o places devolve "Dallas" como [DAL, DFW, ADS], nunca um "todos os
  // aeroportos". Addison (ADS) fica de fora de propósito — aviação geral não é
  // escolha de quem compra passagem.
  {
    iata: 'DFW',
    display: 'Dallas/Fort Worth',
    names: ['dallas/fort worth', 'dallas fort worth', 'dallas-fort worth'],
  },
  { iata: 'DAL', display: 'Love Field', names: ['love field', 'dallas love field'] },
  { iata: 'ORD', display: "O'Hare", names: ["o'hare", 'ohare', 'o hare'] },
  { iata: 'MDW', display: 'Midway', names: ['midway'] },
  { iata: 'IAH', display: 'George Bush', names: ['george bush', 'bush intercontinental'] },
  { iata: 'HOU', display: 'Hobby', names: ['hobby', 'william p. hobby', 'william p hobby'] },
  { iata: 'YYZ', display: 'Pearson', names: ['pearson'] },
  { iata: 'YTZ', display: 'Billy Bishop', names: ['billy bishop'] },
  { iata: 'HND', display: 'Haneda', names: ['haneda'] },
  { iata: 'NRT', display: 'Narita', names: ['narita'] },
  { iata: 'FCO', display: 'Fiumicino', names: ['fiumicino', 'leonardo da vinci'] },
  { iata: 'CIA', display: 'Ciampino', names: ['ciampino'] },
  { iata: 'ICN', display: 'Incheon', names: ['incheon'] },
  { iata: 'GMP', display: 'Gimpo', names: ['gimpo'] },
  { iata: 'LHR', display: 'Heathrow', names: ['heathrow'] },
  { iata: 'LGW', display: 'Gatwick', names: ['gatwick'] },
  { iata: 'CDG', display: 'Charles de Gaulle', names: ['charles de gaulle', 'roissy'] },
  { iata: 'ORY', display: 'Orly', names: ['orly'] },
  { iata: 'EZE', display: 'Ezeiza', names: ['ezeiza'] },
  { iata: 'AEP', display: 'Aeroparque', names: ['aeroparque'] },
  { iata: 'OPO', display: 'Porto', names: ['porto', 'francisco sa carneiro'] },
];

/** A curadoria como o TESTE a inspeciona (a invariante "os dois sentidos cobrem
 *  o mesmo conjunto" é de construção; o que sobra a testar é a forma de cada
 *  entrada). Não é para consumo em produção: use os dois resolvedores abaixo. */
export function curatedAirports(): ReadonlyArray<CuratedAirport> {
  return AIRPORTS_BY_NAME;
}

const NAME_TO_IATA = new Map<string, string>(
  AIRPORTS_BY_NAME.flatMap((a) => a.names.map((name) => [name, a.iata] as const)),
);

const IATA_TO_DISPLAY = new Map<string, string>(
  AIRPORTS_BY_NAME.map((a) => [a.iata, a.display] as const),
);

const AIRPORT_PREFIX = /^aeroporto(\s+internacional)?\s+(de|do|da|del|of)\s+/;

function foldAirportName(term: string): string {
  return term
    .normalize('NFD')
    .replaceAll(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase()
    .replaceAll(/\s+/g, ' ')
    .replace(AIRPORT_PREFIX, '');
}

/** IATA do aeroporto nomeado popularmente; `null` para cidade, desconhecido ou vazio. */
export function airportByPopularName(term: string | null | undefined): string | null {
  if (!term) return null;
  const key = foldAirportName(term);
  return key ? (NAME_TO_IATA.get(key) ?? null) : null;
}

/** Nome popular de EXIBIÇÃO do aeroporto ("Viracopos", "Guarulhos", "Galeão");
 *  `null` sem curadoria. IATA em qualquer caixa. */
export function airportPopularName(iata: string | null | undefined): string | null {
  if (!iata) return null;
  return IATA_TO_DISPLAY.get(iata.trim().toUpperCase()) ?? null;
}
